import React, { useState, useMemo } from 'react'
import FormGenerator from 'Presentation/Components/Form/FormGenerator'
import { VariantItem } from './Components/VariantItem'
// import VariantTable from './Components/VariantTable'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { isMobileDevice } from '_metronic/assets/ts/_utils'
import { KTSVG } from '_metronic/helpers'
import VariantTable2 from './Components/VariantTable2'
import ViewModel from '../../ViewModel'

export interface ItemInputState {
	price: string
	sku: string
	stockQuantity: number
}

export interface ItemState {
	id: number
	order: number
	name: string
	price: string
	sku: string
	stockQuantity: number
}

export interface InnerItemState {
	order: number
	name: string
	values: ItemState[]
}

const INITIAL_ITEM_STATE: ItemState = {
	id: 0,
	order: 0,
	name: '',
	price: '',
	sku: '',
	stockQuantity: 0,
}

const INITIAL_INNER_ITEM_STATE: InnerItemState = {
	name: '',
	order: 0,
	values: [],
}

const INITIAL_FORM_STATE = {
	isOpenProductVariantOptional: false,
	price: 0,
	stockQuantity: 0,
	variants: {
		name: '',
		items: [] as ItemState[],
		isUseInnerVariants: false,
		innerVariants: {
			name: '',
			items: [] as InnerItemState[],
		},
	},
}

const validationSchema = Yup.object().shape({
	productRows: Yup.array().of(
		Yup.object().shape({
			size: Yup.string().required('Size is required'),
			color: Yup.string().required('Color is required'),
			price: Yup.string().required('Price is required'),
			stock: Yup.string().required('Stock is required'),
			sku: Yup.string().required('SKU is required'),
		})
	),
	orderquantity: Yup.string().required('Order quantity is required'),
})

const ProductOptionalView: React.FC = () => {
	const { showDeliveryVariant } = ViewModel()
	const [formState, setFormState] = useState(INITIAL_FORM_STATE)
	const canUseInnerVariant = useMemo(
		() =>
			formState.variants.isUseInnerVariants === false &&
			formState.isOpenProductVariantOptional === true,
		[formState.variants.isUseInnerVariants, formState.isOpenProductVariantOptional]
	)

	const formik = useFormik({
		initialValues: {
			orderquantity: '1',
		},
		validationSchema,
		onSubmit: values => {
			console.log('Form data:', values)
		},
	})

	const onUseVariant = () => {
		setFormState(prevState => ({
			...prevState,
			isOpenProductVariantOptional: true,
			variants: {
				...prevState.variants,
				items: [
					{
						...INITIAL_ITEM_STATE,
						order: prevState.variants.items.length + 1,
					},
				],
			},
		}))
	}

	const onUseInnerVariant = () => {
		setFormState(prevState => ({
			...prevState,
			variants: {
				...prevState.variants,
				isUseInnerVariants: true,
				innerVariants: {
					...prevState.variants.innerVariants,
					items: [
						{
							...INITIAL_INNER_ITEM_STATE,
							order: prevState.variants.innerVariants.items.length + 1,
							values: [...Array(prevState.variants.items.length)].map((_, idx) => ({
								...INITIAL_ITEM_STATE,
								order: idx + 1,
							})),
						},
					],
				},
			},
		}))
	}

	const onChangeVariantName = (isInnerVariant: boolean, e: React.ChangeEvent<HTMLInputElement>) => {
		if (isInnerVariant) {
			setFormState(prevState => ({
				...prevState,
				variants: {
					...prevState.variants,
					innerVariants: {
						...prevState.variants.innerVariants,
						name: e.target.value,
					},
				},
			}))
		} else {
			setFormState(prevState => ({
				...prevState,
				variants: {
					...prevState.variants,
					name: e.target.value,
				},
			}))
		}
	}

	const onChangeVariantItem = (index: number, key: string, value: string) => {
		const findedItem = formState.variants.items.find((_, i) => i === index)
		const newItems = [...formState.variants.items].map((item, i) =>
			i === index ? { ...item, [key]: value } : item
		)
		let newInnerItems = [...formState.variants.innerVariants.items]
		if (findedItem?.name === '' && key === 'name') {
			newItems.push({
				...INITIAL_ITEM_STATE,
				order: formState.variants.items.length + 1,
			})

			if (formState.variants.isUseInnerVariants) {
				newInnerItems = newInnerItems.map(innerItem => ({
					...innerItem,
					values: [
						...innerItem.values,
						{ ...INITIAL_ITEM_STATE, order: innerItem.values.length + 1 },
					],
				}))
			}
		}

		setFormState(prevState => ({
			...prevState,
			variants: {
				...prevState.variants,
				items: newItems,
				innerVariants: {
					...prevState.variants.innerVariants,
					items: newInnerItems,
				},
			},
		}))
	}

	const onChangeInnerVariantItemName = (index: number, key: string, value: string) => {
		const findedItem = formState.variants.innerVariants.items.find((_, i) => i === index)
		let newItems = [...formState.variants.innerVariants.items].map((item, i) =>
			i === index ? { ...item, [key]: value } : item
		)
		if (findedItem?.name === '' && key === 'name') {
			newItems = newItems.map((innerItem, idx) => {
				if (idx === index) {
					return {
						...innerItem,
						values: [
							...innerItem.values,
							{ ...INITIAL_ITEM_STATE, order: innerItem.values.length + 1 },
						],
					}
				} else {
					return innerItem
				}
			})
		}

		setFormState(prevState => ({
			...prevState,
			variants: {
				...prevState.variants,
				innerVariants: {
					...prevState.variants.innerVariants,
					items: newItems,
				},
			},
		}))
	}

	return (
		<div className="card border border-kumopack-grey-200">
			<div className="card-header border-0 mt-4">
				<div className="card-title">
					<div>
						<h4 className="text-kumopack-grey-900 fw-bold mb-2">3. ข้อมูลการขาย</h4>
						<div className="text-kumopack-grey-600 fs-6">ตัวเลือกผลิตภัณฑ์</div>
					</div>
				</div>
				<div className="mt-2 separator border-1px w-100 border-kumopack-grey-200"></div>
			</div>

			<div className="card-body">
				<form onSubmit={formik.handleSubmit}>
					<div className={`mb-5 d-flex flex-column flex-xl-row`}>
						<div className="col-12 col-xl-12">
							<div className="card bg-kumopack-grey-100">
								<div className="card-body">
									{/* Not use Variant Optional */}
									{!formState.isOpenProductVariantOptional && (
										<React.Fragment>
											<div className="row gy-5">
												<label
													className={`form-label col-12 col-xl-3 mb-0 d-flex flex-row mb-3`}
													data-testid="form-input-label-component">
													<div className="d-flex flex-column">
														<span>ตัวเลือกสินค้า</span>
													</div>
												</label>

												<div className="col-12 col-xl-9">
													<button
														type="button"
														className="btn btn-sm btn-kumopack-secondary-700 text-kumopack-base-white fw-bold"
														onClick={onUseVariant}>
														<img src="/media/icons/kumopack/plus-white.svg" alt="Plus Icon" />
														เปิดใช้งานตัวเลือกสินค้า
													</button>
												</div>
											</div>
										</React.Fragment>
									)}

									{/* Use Variant Optional */}
									{formState.isOpenProductVariantOptional && (
										<React.Fragment>
											<div className="row gy-5">
												<label
													className={`form-label col-12 col-xl-3 mb-0 d-flex flex-row mb-3`}
													data-testid="form-input-label-component">
													<div className="d-flex flex-column">
														<span>ตัวเลือกสินค้าที่ 1</span>
													</div>
												</label>

												<div className="col-12 col-xl-9">
													<div className="d-flex flex-row justify-content-between">
														<FormGenerator
															formKey="variantName"
															inputType="plain"
															placeholder="ใส่ค่า"
															additionalClassName="form-control-sm w-300px"
															value={formState.variants.name}
															onChange={e =>
																onChangeVariantName(false, e as React.ChangeEvent<HTMLInputElement>)
															}
														/>

														<button className="btn btn-sm btn-light-danger p-3">
															<KTSVG
																path="/media/icons/duotune/arrows/arr015.svg"
																className="svg-icon mx-auto"
															/>
														</button>
													</div>

													<hr />

													<div className="row g-5">
														{formState.variants.items.map((item, index) => (
															<div className="col-12 col-xl-6" key={`variant-${index}`}>
																<VariantItem
																	showImage={false}
																	disableDelete={index === 0}
																	onChange={value => onChangeVariantItem(index, 'name', value)}
																/>
															</div>
														))}
													</div>
												</div>
											</div>

											{formState.variants.isUseInnerVariants && (
												<div className="row gy-5 mt-5">
													<label
														className={`form-label col-12 col-xl-3 mb-0 d-flex flex-row mb-3`}
														data-testid="form-input-label-component">
														<div className="d-flex flex-column">
															<span>ตัวเลือกสินค้าที่ 2</span>
														</div>
													</label>

													<div className="col-12 col-xl-9">
														<div className="d-flex flex-row justify-content-between">
															<FormGenerator
																formKey="innerVariantName"
																inputType="plain"
																placeholder="ใส่ค่า"
																additionalClassName="form-control-sm w-300px"
																value={formState.variants.innerVariants.name}
																onChange={e =>
																	onChangeVariantName(
																		true,
																		e as React.ChangeEvent<HTMLInputElement>
																	)
																}
															/>

															<button className="btn btn-sm btn-light-danger p-3">
																<KTSVG
																	path="/media/icons/duotune/arrows/arr015.svg"
																	className="svg-icon mx-auto"
																/>
															</button>
														</div>

														<hr />

														<div className="row g-5">
															{formState.variants.innerVariants.items.map((item, index) => (
																<div className="col-12 col-xl-6" key={`inner-variant-${index}`}>
																	<VariantItem
																		showImage={false}
																		disableDelete={index === 0}
																		onChange={value =>
																			onChangeInnerVariantItemName(index, 'name', value)
																		}
																	/>
																</div>
															))}
														</div>
													</div>
												</div>
											)}
										</React.Fragment>
									)}

									{canUseInnerVariant && (
										<button
											type="button"
											className="btn btn-sm btn-kumopack-secondary-700 text-kumopack-base-white fw-bold"
											onClick={onUseInnerVariant}>
											เพิ่มตัวเลือกสินค้า 2
										</button>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Variant Item Table */}
					{formState.isOpenProductVariantOptional && (
						<div className="col-12">
							<div className="row gy-5">
								<label className="form-label col-12 col-xl-3  mb-3">
									<div className="d-flex flex-column">
										<span>รายการตัวเลือกสินค้า</span>
									</div>
								</label>
								<div className="col-12 col-xl-9">
									{/* <VariantTable
										name={formState.variants.name}
										items={formState.variants.items}
										isUseInnerVariants={formState.variants.isUseInnerVariants}
										innerVariants={formState.variants.innerVariants}
										onChange={onChangeVariantItem}
									/> */}

									<VariantTable2 showDeliveryVariant={showDeliveryVariant} />
								</div>
							</div>
						</div>
					)}

					{/* Quantity */}
					<div className="col-12 col-xl-12">
						<div className="row gy-5">
							<label
								className={`form-label col-12 col-xl-3 mb-0 d-flex flex-row mb-3`}
								data-testid="form-input-label-component">
								<div className="d-flex flex-column">
									<span>จำนวนการซื้อขั้นต่ำ</span>
								</div>
							</label>
							<div className="col-12 col-xl-9">
								<div
									className={`form-group position-relative ${
										!isMobileDevice() ? 'form-horizontal' : ''
									}`}>
									<input
										id="orderquantity"
										name="orderquantity"
										type="text"
										className={`form-control ${
											formik.touched.orderquantity && formik.errors.orderquantity
												? 'is-invalid'
												: ''
										}`}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.orderquantity}
									/>
									<span
										className={`form-unit ${
											formik.touched.orderquantity && formik.errors.orderquantity ? 'd-none' : ''
										}`}>
										ชิ้น
									</span>
									{formik.touched.orderquantity && formik.errors.orderquantity ? (
										<div className="invalid-feedback">{formik.errors.orderquantity}</div>
									) : null}
								</div>
								<div className="text-kumopack-grey-400 w-75 mt-2">
									จำนวนการซื้อขั้นต่ำ หมายถึงจำนวนสินค้าอย่างน้อยที่สุดที่ผู้ซื้อสามารถ
									สั่งซื้อสินค้าชิ้นนี้ได้ หากจำนวนสินค้าในคลังของคุณมีน้อยกว่าจำนวน สินค้าขั้นต่ำ
									ผู้ซื้อจะไม่สามารถสั่งซื้อสินค้าชิ้นนี้ได้
								</div>
							</div>
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}

export default ProductOptionalView
